import React, { useState, useRef, useEffect } from 'react';
import { fetchOpenAIText } from '../../utils/openaiClient';
import './EssentialVocabularyExercise.css';

const EssentialVocabularyExercise = ({ vocabulary: initialVocabulary, title, level, onClose, updateAttributes, node, editor, getPos, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  // Normalize vocabulary items to ensure they have a type field
  const normalizeVocabulary = (vocab) => (vocab || []).map(item => ({
    ...item,
    type: item.type || 'word'
  }));
  
  const [vocabulary, setVocabulary] = useState(normalizeVocabulary(initialVocabulary));
  const [editing, setEditing] = useState({ idx: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title || 'Essential Vocabulary');
  const [editableLevel, setEditableLevel] = useState(level || 'B1');
  const [editableWords, setEditableWords] = useState(normalizeVocabulary(initialVocabulary));
  const [flipped, setFlipped] = useState({});
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'list'

  // Restore on mount
  useEffect(() => {
    if (myState.flipped && Object.keys(myState.flipped).length > 0) setFlipped(myState.flipped);
    else if (otherState.flipped && Object.keys(otherState.flipped).length > 0) setFlipped(otherState.flipped);
    if (myState.viewMode) setViewMode(myState.viewMode);
    else if (otherState.viewMode) setViewMode(otherState.viewMode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: mirror other side's flipped cards
  useEffect(() => {
    if (otherState.flipped && typeof otherState.flipped === 'object') setFlipped(otherState.flipped);
  }, [otherState.flipped]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: mirror other side's view mode
  useEffect(() => {
    if (otherState.viewMode) setViewMode(otherState.viewMode);
  }, [otherState.viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const rowRefs = useRef({});
  const genTimersRef = useRef({});
  const lastGeneratedRef = useRef({});
  const [isGenerating, setIsGenerating] = useState({});

  const autoResizeRow = (index) => {
    const refs = rowRefs.current[index];
    if (!refs) return;
    const { word, def, translation } = refs;
    if (word) {
      word.style.height = 'auto';
      word.style.height = `${word.scrollHeight}px`;
    }
    if (def) {
      def.style.height = 'auto';
      def.style.height = `${def.scrollHeight}px`;
    }
    if (translation) {
      translation.style.height = 'auto';
      translation.style.height = `${translation.scrollHeight}px`;
    }
    if (word && def) {
      const maxH = Math.max(word.scrollHeight, def.scrollHeight);
      word.style.height = `${maxH}px`;
      def.style.height = `${maxH}px`;
    }
  };

  const registerRef = (index, key) => (el) => {
    if (!rowRefs.current[index]) rowRefs.current[index] = {};
    rowRefs.current[index][key] = el;
    if (el) {
      requestAnimationFrame(() => autoResizeRow(index));
    }
  };

  useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        vocabulary.forEach((_, i) => autoResizeRow(i));
      });
    }
  }, [isEditing, vocabulary]);

  // Handle paste at component level to create items from multi-line or comma-separated text
  useEffect(() => {
    if (!isEditing) return;

    const handlePaste = (e) => {
      // Only handle if pasting outside of input fields
      const target = e.target;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) {
        return; // Let textarea handle it
      }

      const pastedText = e.clipboardData?.getData('text/plain');
      if (!pastedText || !pastedText.trim()) return;

      // Split by newlines first, then by commas within each line
      const lines = pastedText.split(/\r?\n/);
      const items = [];
      
      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) return;
        
        // Split by comma, but keep each part as a complete phrase
        const commaParts = trimmed.split(',').map(part => part.trim()).filter(part => part.length > 0);
        
        commaParts.forEach(phrase => {
          items.push(phrase); // Each phrase stays intact - no word splitting
        });
      });

      if (items.length <= 1) return; // Single item, let default behavior handle it

      e.preventDefault();
      e.stopPropagation();

      // Create new items for each phrase (each stays as one complete unit)
      const newItems = items.map((phrase, phraseIndex) => {
        const trimmedPhrase = phrase.trim();
        const hasSpaces = trimmedPhrase.includes(' ');
        const hasHyphen = trimmedPhrase.includes('-');
        let detectedType = 'word';

        // Auto-detect type for multi-word items
        if (hasSpaces || hasHyphen) {
          const phraseLower = trimmedPhrase.toLowerCase();
          const idiomPatterns = [
            'break the', 'piece of', 'out of', 'in the', 'on the', 'at the',
            'by the', 'for the', 'with the', 'under the', 'over the',
            'once in a', 'once upon a', 'all of a', 'out of the'
          ];
          const isIdiom = idiomPatterns.some(pattern => phraseLower.includes(pattern));

          const wordCount = trimmedPhrase.split(/\s+/).filter(w => w.length > 0).length;
          if (isIdiom) {
            detectedType = 'idiom';
          } else if (wordCount === 2) {
            detectedType = 'collocation';
          } else {
            detectedType = 'phrase';
          }
        }

        return {
          word: trimmedPhrase, // Keep entire phrase intact
          definition: '',
          type: detectedType,
          id: `vocab_${Date.now()}_${phraseIndex}_${Math.random()}`
        };
      });

      // Add all new items to vocabulary
      setVocabulary(prev => [...prev, ...newItems]);
      setEditableWords(prev => [...prev, ...newItems]);
    };

    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isEditing]);

  // Sync state with props when they change externally (but not when editing)
  // This helps preserve state when component remounts after mode switch
  useEffect(() => {
    if (!isEditing) {
      // Ensure all items have a type field (default to 'word' if missing)
      const normalizedVocabulary = (initialVocabulary || []).map(item => ({
        ...item,
        type: item.type || 'word'
      }));
      setVocabulary(normalizedVocabulary);
      setEditableWords([...normalizedVocabulary]);
      setEditableTitle(title || 'Essential Vocabulary');
      setEditableLevel(level || 'B1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVocabulary, title, level, isEditing]);

  useEffect(() => {
    return () => {
      const timers = genTimersRef.current || {};
      Object.values(timers).forEach(id => clearTimeout(id));
      genTimersRef.current = {};
    };
  }, []);

  const maybeGenerateDefinition = async (index, wordValue) => {
    const word = String(wordValue || '').trim();
    if (!word) return;
    const current = vocabulary[index];
    if (!current || (current.definition && current.definition.trim().length > 0)) return;
    if (lastGeneratedRef.current[index] === word) return;

    const levelSpecs = {
      A1: { maxWords: 10, temp: 0.1, style: 'Use A1 words only. One simple clause. No idioms. Avoid abstract terms.', example: 'big: large in size; quiet: making little sound' },
      A2: { maxWords: 14, temp: 0.1, style: 'Use A2 everyday words. One simple sentence. No relative clauses or advanced words.', example: 'borrow: take and return later; crowded: full of people' },
      B1: { maxWords: 18, temp: 0.15, style: 'Use B1 words. Clear neutral style. Avoid academic/rare terms.', example: 'flexible: can change or bend easily; efficient: doing work well without waste' },
      B2: { maxWords: 20, temp: 0.2, style: 'Use B2 words. You may use one relative clause. Keep concise.', example: 'sustainable: can continue without causing harm; justify: give good reasons for' },
      C1: { maxWords: 22, temp: 0.2, style: 'Advanced but concise academic tone (C1). Prefer precise, high-utility words.', example: 'ambiguous: having more than one possible meaning; facilitate: make a process easier' },
      C2: { maxWords: 22, temp: 0.2, style: 'Very advanced, precise and concise (C2). One sentence only.', example: 'ubiquitous: present everywhere; empirical: based on observed evidence' },
    };
    const spec = levelSpecs[editableLevel] || { maxWords: 18, temp: 0.15, style: 'Use intermediate words. Clear structure.', example: '' };

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const targetRe = new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i');

    const buildPrompt = (strict = false) => `You are a CEFR-aware lexicographer. Write ONE short English definition that matches the target level.\n\nTarget word/phrase: "${word}"\nLevel: ${editableLevel}\nGuidelines: ${spec.style}\nStyle examples for this level (do not copy, only mimic complexity): ${spec.example}\nHard constraints:\n- Do NOT use the target word (or any inflected form) in the definition\n- Exactly one sentence\n- Max ${spec.maxWords} words\n- Plain text only (no quotes, no markdown, no examples)\n- No meta phrases like "a word that means" or "term for"\n${strict ? '- STRICT retry: If uncertain, prefer a generic hypernym that fits the level.' : ''}`;

    const countWords = (s) => (String(s || '').trim().split(/\s+/).filter(Boolean).length);
    const tooComplexFor = (s, lvl) => {
      const txt = String(s || '');
      if (lvl === 'A1' || lvl === 'A2') {
        if (/[,:;]/.test(txt)) return true;
        if (/(which|that|because|although|despite|furthermore|however|therefore)\b/i.test(txt)) return true;
      }
      return false;
    };

    try {
      setIsGenerating(prev => ({ ...prev, [index]: true }));
      let text = await fetchOpenAIText({ prompt: buildPrompt(false), temperature: spec.temp, maxTokens: 120 });
      let firstLine = String(text || '').split('\n')[0].trim();
      if (targetRe.test(firstLine)) {
        text = await fetchOpenAIText({ prompt: buildPrompt(true), temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
        firstLine = String(text || '').split('\n')[0].trim();
        if (targetRe.test(firstLine)) {
          firstLine = firstLine.replace(targetRe, 'the term');
        }
      }
      if (countWords(firstLine) > spec.maxWords || tooComplexFor(firstLine, editableLevel)) {
        const refinePrompt = `Rewrite this definition to strictly match CEFR ${editableLevel} constraints. Keep meaning, but simplify or shorten. One sentence, max ${spec.maxWords} words, ${spec.style}. Do not use the target word.\nDefinition: ${firstLine}`;
        const refined = await fetchOpenAIText({ prompt: refinePrompt, temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
        const refinedLine = String(refined || '').split('\n')[0].trim();
        if (refinedLine) firstLine = refinedLine;
      }

      setVocabulary(prev => prev.map((p, i) => i === index ? { ...p, definition: firstLine } : p));
      setEditableWords(prev => prev.map((p, i) => i === index ? { ...p, definition: firstLine } : p));
      requestAnimationFrame(() => autoResizeRow(index));
      lastGeneratedRef.current[index] = word;
    } catch (_) {
      // silent
    } finally {
      setIsGenerating(prev => ({ ...prev, [index]: false }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const startEdit = (idx, field, value) => {
    setEditing({ idx, field });
    setEditValue(value);
  };

  const commitEdit = () => {
    if (editing.idx === null) return;
    setVocabulary(prev => prev.map((item, i) =>
      i === editing.idx ? { ...item, [editing.field]: editValue } : item
    ));
    setEditableWords(prev => prev.map((item, i) =>
      i === editing.idx ? { ...item, [editing.field]: editValue } : item
    ));
    setEditing({ idx: null, field: null });
    setEditValue('');
  };

  // eslint-disable-next-line no-unused-vars
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditing({ idx: null, field: null });
      setEditValue('');
    }
  };

  const handleAddItem = () => {
    // Check if any existing item has translation field to maintain consistency
    const hasTranslation = vocabulary.length > 0 && vocabulary.some(item => item.translation !== undefined);
    const newItem = { 
      word: '', 
      definition: '', 
      type: 'word', 
      id: `vocab_${Date.now()}`,
      ...(hasTranslation && { translation: '' })
    };
    setVocabulary(prev => [...prev, newItem]);
    setEditableWords(prev => [...prev, newItem]);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteItem = (index) => {
    if (vocabulary.length <= 1) return;
    setVocabulary(prev => prev.filter((_, i) => i !== index));
    setEditableWords(prev => prev.filter((_, i) => i !== index));
  };

  const toggleFlip = (index) => {
    setFlipped(prev => {
      const next = { ...prev, [index]: !prev[index] };
      updateState({ flipped: next });
      return next;
    });
  };

  const handleFlipKey = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFlip(index);
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        vocabulary: editableWords,
        title: editableTitle,
        level: editableLevel,
      });
    }
  };

  return (
    <div className="ev-exercise">
      <div className="ev-header">
        <div className="ev-header-left">
          <div className="ev-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="ev-title-input"
            />
          ) : (
            <span className="ev-title">{editableTitle}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={e => setEditableLevel(e.target.value)}
              className="ev-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="ev-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="ev-header-actions">
          {!isEditing && !isStudent && (
            <button className="ev-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {onClose && (
            <button className="ev-icon-btn" aria-label="Close" data-tip="Close" onClick={() => {
              // Auto-save if in edit mode
              if (isEditing && updateAttributes) {
                updateAttributes({
                  vocabulary: editableWords,
                  title: editableTitle,
                  level: editableLevel,
                });
              }
              onClose();
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="ev-body">
        {!isEditing && (
          <div className="ev-view-toggle">
            <button
              type="button"
              className={`ev-view-toggle-btn${viewMode === 'cards' ? ' is-active' : ''}`}
              onClick={() => { setViewMode('cards'); updateState({ viewMode: 'cards' }); }}
            >
              Cards
            </button>
            <button
              type="button"
              className={`ev-view-toggle-btn${viewMode === 'list' ? ' is-active' : ''}`}
              onClick={() => { setViewMode('list'); updateState({ viewMode: 'list' }); }}
            >
              List
            </button>
          </div>
        )}

        {isEditing ? (
          <>
            <div className="ev-edit-list">
              {vocabulary.map((item, i) => (
                <div key={item.id || i} className="ev-edit-row">
                  <div className="ev-row-content">
                    <select
                      value={item.type || 'word'}
                      onChange={e => {
                        const value = e.target.value;
                        setVocabulary(prev => prev.map((p, idx) => idx === i ? { ...p, type: value } : p));
                        setEditableWords(prev => prev.map((p, idx) => idx === i ? { ...p, type: value } : p));
                      }}
                      title="Item type"
                    >
                      <option value="word">Word</option>
                      <option value="phrase">Phrase</option>
                      <option value="idiom">Idiom</option>
                      <option value="expression">Expression</option>
                      <option value="collocation">Collocation</option>
                    </select>
                    <textarea
                      value={item.word}
                      placeholder={
                        item.type === 'word' ? 'Enter word' : 
                        item.type === 'phrase' ? 'Enter phrase (e.g., "take care of")' : 
                        item.type === 'idiom' ? 'Enter idiom (e.g., "break the ice")' : 
                        item.type === 'expression' ? 'Enter expression (e.g., "by the way")' : 
                        'Enter collocation (e.g., "make a decision")'
                      }
                      rows={1}
                      ref={registerRef(i, 'word')}
                      onPaste={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        const pastedText = e.clipboardData.getData('text/plain');
                        
                        // Split by newlines first, then by commas within each line
                        const lines = pastedText.split(/\r?\n/);
                        const items = [];
                        
                        lines.forEach(line => {
                          const trimmed = line.trim();
                          if (!trimmed) return;
                          
                          // Split by comma, but keep each part as a complete phrase
                          const commaParts = trimmed.split(',').map(part => part.trim()).filter(part => part.length > 0);
                          items.push(...commaParts);
                        });
                        
                        if (items.length > 1) {
                          // Multiple items pasted - create new items for each (each stays as complete phrase)
                          const newItems = items.map((phrase, phraseIndex) => {
                            const trimmedPhrase = phrase.trim();
                            const hasSpaces = trimmedPhrase.includes(' ');
                            const hasHyphen = trimmedPhrase.includes('-');
                            let detectedType = 'word';
                            
                            // Auto-detect type for multi-word items
                            if (hasSpaces || hasHyphen) {
                              const phraseLower = trimmedPhrase.toLowerCase();
                              const idiomPatterns = [
                                'break the', 'piece of', 'out of', 'in the', 'on the', 'at the',
                                'by the', 'for the', 'with the', 'under the', 'over the',
                                'once in a', 'once upon a', 'all of a', 'out of the'
                              ];
                              const isIdiom = idiomPatterns.some(pattern => phraseLower.includes(pattern));
                              
                              const wordCount = trimmedPhrase.split(/\s+/).filter(w => w.length > 0).length;
                              if (isIdiom) {
                                detectedType = 'idiom';
                              } else if (wordCount === 2) {
                                detectedType = 'collocation';
                              } else {
                                detectedType = 'phrase';
                              }
                            }
                            
                            return {
                              word: trimmedPhrase, // Keep entire phrase intact - never split by spaces
                              definition: '',
                              type: detectedType,
                              id: `vocab_${Date.now()}_${phraseIndex}_${Math.random()}`
                            };
                          });
                          
                          // Replace current item with first phrase, add others after
                          setVocabulary(prev => {
                            const newVocab = [...prev];
                            newVocab[i] = { ...newVocab[i], word: newItems[0].word, type: newItems[0].type };
                            newVocab.splice(i + 1, 0, ...newItems.slice(1));
                            return newVocab;
                          });
                          
                          setEditableWords(prev => {
                            const newWords = [...prev];
                            newWords[i] = { ...newWords[i], word: newItems[0].word, type: newItems[0].type };
                            newWords.splice(i + 1, 0, ...newItems.slice(1));
                            return newWords;
                          });
                        } else {
                          // Single item - paste as is (entire text is one phrase/word, never split)
                          const value = items[0] || pastedText.trim();
                          setVocabulary(prev => prev.map((p, idx) => idx === i ? { ...p, word: value } : p));
                          setEditableWords(prev => prev.map((p, idx) => idx === i ? { ...p, word: value } : p));
                          requestAnimationFrame(() => autoResizeRow(i));
                          if (genTimersRef.current[i]) clearTimeout(genTimersRef.current[i]);
                          genTimersRef.current[i] = setTimeout(() => {
                            maybeGenerateDefinition(i, value);
                          }, 700);
                        }
                      }}
                      onChange={e => {
                        const value = e.target.value;
                        setVocabulary(prev => prev.map((p, idx) => idx === i ? { ...p, word: value } : p));
                        setEditableWords(prev => prev.map((p, idx) => idx === i ? { ...p, word: value } : p));
                        requestAnimationFrame(() => autoResizeRow(i));
                        if (genTimersRef.current[i]) clearTimeout(genTimersRef.current[i]);
                        genTimersRef.current[i] = setTimeout(() => {
                          maybeGenerateDefinition(i, value);
                        }, 700);
                      }}
                      className="word-input"
                    />
                    <span className="definition-wrapper">
                      <textarea
                        value={item.definition}
                        placeholder="Definition"
                        rows={1}
                        ref={registerRef(i, 'def')}
                        onChange={e => {
                          const value = e.target.value;
                          setVocabulary(prev => prev.map((p, idx) => idx === i ? { ...p, definition: value } : p));
                          setEditableWords(prev => prev.map((p, idx) => idx === i ? { ...p, definition: value } : p));
                          requestAnimationFrame(() => autoResizeRow(i));
                        }}
                        className="definition-input"
                      />
                      {isGenerating[i] && <span className="gen-spinner" aria-label="Generating definition" />}
                    </span>
                    {item.translation !== undefined && (
                      <span className="translation-wrapper">
                        <textarea
                          value={item.translation || ''}
                          placeholder="Translation"
                          rows={1}
                          ref={registerRef(i, 'translation')}
                          onChange={e => {
                            const value = e.target.value;
                            setVocabulary(prev => prev.map((p, idx) => idx === i ? { ...p, translation: value } : p));
                            setEditableWords(prev => prev.map((p, idx) => idx === i ? { ...p, translation: value } : p));
                            requestAnimationFrame(() => autoResizeRow(i));
                          }}
                        />
                      </span>
                    )}
                  </div>
                  {vocabulary.length > 1 && (
                    <button
                      className="ev-edit-remove-btn"
                      onClick={() => {
                        setVocabulary(prev => prev.filter((_, idx) => idx !== i));
                        setEditableWords(prev => prev.filter((_, idx) => idx !== i));
                      }}
                      title="Delete item"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={handleAddItem}
              className="ev-add-btn"
              title="Add vocabulary item"
            >
              + Add new item
            </button>
          </>
        ) : (
          <>
            {viewMode === 'list' ? (
              <div className="vocab-list-view">
                {vocabulary.map((item, i) => (
                  <div key={item.id || i} className="vocab-list-item">
                    <div className="vocab-list-word">
                      <span className="vocab-word">{item.word}</span>
                      {(item.type && item.type !== 'word') && (
                        <span className="vocab-pos">{item.type}</span>
                      )}
                      {item.partOfSpeech && <span className="vocab-pos">{item.partOfSpeech}</span>}
                    </div>
                    <div className="vocab-list-def">{item.definition}</div>
                    {item.translation && (
                      <div className="vocab-list-translation">{item.translation}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="vocab-cards-grid">
                {vocabulary.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`vocab-card ${flipped[index] ? 'flipped' : ''}`}
                    onClick={() => toggleFlip(index)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={!!flipped[index]}
                    aria-label={flipped[index] ? 'Hide definition' : 'Show definition'}
                    onKeyDown={(e) => handleFlipKey(e, index)}
                  >
                    <div className="vocab-card-inner">
                      <div className="vocab-card-front">
                        <span className="vocab-card-flip-hint" aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                            <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                          </svg>
                        </span>
                        <div className="vocab-word">{item.word}</div>
                        {(item.type && item.type !== 'word') || item.partOfSpeech ? (
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                            {(item.type && item.type !== 'word') && (
                              <span className="vocab-pos">{item.type}</span>
                            )}
                            {item.partOfSpeech && <span className="vocab-pos">{item.partOfSpeech}</span>}
                          </div>
                        ) : null}
                      </div>
                      <div className="vocab-card-back">
                        <div className="vocab-definition">{item.definition}</div>
                        {item.translation && (
                          <div className="vocab-card-translation">{item.translation}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {isEditing && (
        <div className="ev-footer">
          <button className="ev-cancel-btn" onClick={() => {
            setIsEditing(false);
            setEditableTitle(title || 'Essential Vocabulary');
            setEditableLevel(level || 'B1');
            const normalized = normalizeVocabulary(initialVocabulary);
            setVocabulary(normalized);
            setEditableWords([...normalized]);
          }}>Cancel</button>
          <button className="ev-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default EssentialVocabularyExercise; 